import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'latest-routes');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.id === activeCategory) || categories[0];
  const categoryArticles = getArticlesByCategory(activeCategory);

  const pageTitle = `${currentCategory.name} - 爱情岛一论坛免费路线`;
  const pageDesc = currentCategory.description;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory.name },
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`爱情岛一论坛${currentCategory.name},爱情岛论坛免费入口,爱情岛免费线路访问,爱情岛论坛路线更新`}
        canonicalUrl={`https://aiqingdao-line.com/category/${activeCategory}`}
      />
      <ScrollToTop />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="mb-10">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                  {currentCategory.name}
                </h1>
                <p className="text-foreground-600 text-base max-w-2xl">{currentCategory.description}</p>
              </div>

              <div className="flex flex-wrap gap-2 mb-10">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                    }`}
                  >
                    {cat.name}
                    <span className="ml-1.5 opacity-70">({cat.count})</span>
                  </Link>
                ))}
              </div>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group p-6 rounded-2xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium">
                          {currentCategory.name}
                        </span>
                        <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
                      </div>
                      <h2 className="text-base font-semibold font-heading text-foreground-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 rounded-md bg-background-200 text-foreground-500 text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-background-200/70">
                        <span className="text-xs text-foreground-500">{article.publishedAt}</span>
                        <span className="text-xs text-primary-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                          阅读全文
                          <i className="ri-arrow-right-s-line text-sm"></i>
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}